/* =========================================
   GLOBAL TUNING
   ========================================= */

const NANCO_PRICE_AFTER_FIRST_MS = 0;
const NANCO_PRICE_TOTAL_MS = Math.max(1, NANCO_PRICE_AFTER_FIRST_MS + 1);

if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this._onChange = this._onChange.bind(this);
        this._onPointerDownCapture = this._onPointerDownCapture.bind(this);
        this._priceObserver = null;
        this._readyTimer = null;
        this._startSig = '';
        this._firstSig = '';
        this._sawFirstChange = false;

        this._variantUpdateObserver = null;
        this._variantUpdatePoll = null;
        this._variantUpdateTimeout = null;
        this._variantSwitchStartId = '';
        this._isVariantUpdating = false;

        this._isApplyingAvailability = false;
      }

      /* -------------------------
         B2B Pricing Integration
      ------------------------- */

      getCurrentVariantId() {
        const root = this.closest('product-info') || document;
        const input = root.querySelector(
          'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"], input[name="variant_id"]'
        );
        const raw =
          (input && (input.value || input.getAttribute('value'))) ||
          new URLSearchParams(window.location.search).get('variant') ||
          '';
        const m = String(raw).trim().match(/(\d+)$/);
        return m ? m[1] : String(raw || '').trim();
      }

      getProductForm() {
        const root =
          this.closest('product-info') ||
          this.closest('[id^="shopify-section-"]') ||
          this.closest('[data-section-id]') ||
          document;

        return (
          root.querySelector('form[data-type="add-to-cart-form"]') ||
          root.querySelector('form[action*="/cart/add"]')
        );
      }

      getVariantIdInput() {
        const form = this.getProductForm();
        if (!form) return null;

        return (
          form.querySelector('input.product-variant-id[name="id"]') ||
          form.querySelector('input[name="id"]') ||
          form.querySelector('select[name="id"]') ||
          form.querySelector('input[name="variant_id"]')
        );
      }

      getAddToCartButton() {
        const form = this.getProductForm();
        if (!form) return null;

        return (
          form.querySelector('[data-add-to-cart-button]') ||
          form.querySelector('button[name="add"]') ||
          form.querySelector('button[type="submit"]')
        );
      }

      setAddToCartDisabled(disabled) {
        const btn = this.getAddToCartButton();
        if (!btn) return;

        if (disabled) {
          btn.disabled = true;
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
          return;
        }

        // Never force-enable a sold-out variant.
        this.syncAddToCartAvailability();
      }

      clearVariantUpdateWatchers() {
        if (this._variantUpdateObserver) {
          this._variantUpdateObserver.disconnect();
          this._variantUpdateObserver = null;
        }

        if (this._variantUpdatePoll) {
          clearInterval(this._variantUpdatePoll);
          this._variantUpdatePoll = null;
        }

        if (this._variantUpdateTimeout) {
          clearTimeout(this._variantUpdateTimeout);
          this._variantUpdateTimeout = null;
        }
      }

      restoreAddToCartState() {
        this._isVariantUpdating = false;
        this.clearVariantUpdateWatchers();
        this.syncAddToCartAvailability();
      }

      isCurrentVariantAvailable() {
        const variantId = String(this.getCurrentVariantId() || '').trim();
        if (!variantId) return false;

        const variants = this.getVariantData();
        if (!Array.isArray(variants) || !variants.length) {
          const btn = this.getAddToCartButton();
          if (btn?.hasAttribute('disabled')) return false;
          const txt = (btn?.querySelector('.btn__text')?.textContent || btn?.textContent || '').toLowerCase();
          if (txt.includes('sold out') || txt.includes('épuisé') || txt.includes('epuise')) return false;
          return true;
        }

        const variant = variants.find((v) => String(v?.id) === variantId);
        if (!variant) return false;
        return variant.available !== false;
      }

      syncAddToCartAvailability() {
        const btn = this.getAddToCartButton();
        if (!btn) return;

        const available = this.isCurrentVariantAvailable();
        btn.disabled = !available;
        if (!available) {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
          const label = btn.querySelector('.btn__text') || btn.querySelector('span');
          if (label && FoxTheme?.variantStrings?.soldOut) {
            label.textContent = FoxTheme.variantStrings.soldOut;
          }
        } else {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        }

        const idInput = this.getVariantIdInput();
        if (idInput) idInput.disabled = false;
      }

      lockAddToCartUntilVariantUpdates() {
        const idInput = this.getVariantIdInput();
        const btn = this.getAddToCartButton();
        if (!idInput || !btn) return;

        const startValue = String(idInput.value || idInput.getAttribute('value') || '').trim();

        this._isVariantUpdating = true;
        this._variantSwitchStartId = startValue;
        this.setAddToCartDisabled(true);
        this.clearVariantUpdateWatchers();

        const finishIfChanged = () => {
          const currentValue = String(idInput.value || idInput.getAttribute('value') || '').trim();
          if (!currentValue) return;
          if (currentValue === this._variantSwitchStartId) return;

          requestAnimationFrame(() => {
            setTimeout(() => {
              this.restoreAddToCartState();
            }, 0);
          });
        };

        this._variantUpdateObserver = new MutationObserver(() => {
          finishIfChanged();
        });

        try {
          this._variantUpdateObserver.observe(idInput, {
            attributes: true,
            attributeFilter: ['value'],
          });
        } catch (_) {}

        this._variantUpdatePoll = setInterval(() => {
          finishIfChanged();
        }, 25);

        this._variantUpdateTimeout = setTimeout(() => {
          this.restoreAddToCartState();
        }, 800);
      }

      /**
       * Get the product JSON data which contains variant metafields
       */
      getProductJsonData() {
        const selectors = [
          '#RealSizeProductJson',
          '[type="application/json"][id*="ProductJson"]',
          '[type="application/json"][data-product-json]',
          'script[id*="product-json"]',
          'script[data-product-json]',
        ];
        for (const sel of selectors) {
          const el = document.querySelector(sel);
          if (el) {
            try {
              const p = JSON.parse(el.textContent || 'null');
              if (p && Array.isArray(p.variants)) return p;
            } catch (_) {}
          }
        }
        const g = window.product || window.__product || window.FoxTheme?.product;
        if (g && Array.isArray(g.variants)) return g;
        return null;
      }

      /**
       * Try to get the B2B metafield price for a given variant ID.
       * Returns the price in cents (multiplied by 100) or null.
       */
      getMetafieldPrice(variantId) {
        const product = this.getProductJsonData();
        if (!product) return null;

        const variant = product.variants.find(
          (v) => String(v.id) === String(variantId)
        );
        if (!variant) return null;

        const customerTags = (window.__b2bCustomerTags || window.meta?.page?.customerTags || '').toLowerCase();
        const isWeb = customerTags.includes('web');

        const metafields = variant.metafields || variant.metafield || {};
        const custom = metafields.custom || metafields;

        let price = null;

        if (isWeb) {
          price = custom.price_web ?? custom['price_web'] ?? null;
        }
        if (price == null) {
          price = custom.price_base ?? custom['price_base'] ?? null;
        }

        if (price != null && price !== '' && !isNaN(Number(price))) {
          return Math.round(Number(price) * 100);
        }

        return null;
      }

      /**
       * Format cents to money string matching Shopify money format
       */
      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        const fmt = window.Shopify?.locale === 'fr'
          ? amount.replace('.', ',') + '$'
          : '$' + amount;
        if (window.Shopify?.formatMoney) {
          try { return window.Shopify.formatMoney(cents); } catch (_) {}
        }
        if (window.theme?.moneyFormat || window.Shopify?.money_format) {
          const mf = window.theme?.moneyFormat || window.Shopify?.money_format;
          return mf
            .replace('{{amount}}', amount)
            .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
            .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
            .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString());
        }
        return fmt;
      }

      /**
       * Apply metafield price directly to the DOM price elements
       */
      applyMetafieldPrice(variantId) {
        const priceCents = this.getMetafieldPrice(variantId);
        if (priceCents == null) return false;

        const root = this.getSectionRoot();
        const priceFormatted = this.formatMoney(priceCents);

        const priceSelectors = [
          '.price-item--regular',
          '.price-item--sale',
          '.price__regular .price-item',
          '.price__sale .price-item',
          '.f-price-item--regular',
          '.f-price-item',
          '.product__price .money',
          '.product-single__price .money',
          '[data-product-price]',
        ];

        let applied = false;
        for (const sel of priceSelectors) {
          const els = root.querySelectorAll(sel);
          els.forEach((el) => {
            if (el.closest('.price__sale s') || el.closest('s') || el.closest('del')) return;
            el.textContent = priceFormatted;
            el.classList.add('b2b-price-ready', 'b2b-price-applied');
            applied = true;
          });
        }

        if (applied) {
          root.querySelectorAll('.price__sale s, .price__sale del, .price .badge--sale, .price--on-sale .price__badge-sale').forEach((el) => {
            el.style.display = 'none';
          });
          root.querySelectorAll('.price--on-sale').forEach((el) => {
            el.classList.remove('price--on-sale');
          });
        }

        return applied;
      }

      triggerB2BRefresh(reason = 'variant-selects') {
        document.querySelectorAll('.b2b-price-ready, .b2b-price-applied').forEach((el) => {
          el.classList.remove('b2b-price-ready', 'b2b-price-applied');
          delete el.dataset.b2bApplied;
          delete el.dataset.b2bOriginalHtml;
        });

        const variantId = this.getCurrentVariantId();
        if (variantId) {
          const tryApply = () => this.applyMetafieldPrice(variantId);
          tryApply();
          setTimeout(tryApply, 80);
          setTimeout(tryApply, 200);
          setTimeout(tryApply, 500);
        }

        if (!window.B2BPricing) return;

        window.B2BPricing._lastAppliedVariantId = null;

        if (typeof window.B2BPricing.scheduleVariantRefresh === 'function') {
          window.B2BPricing.scheduleVariantRefresh();
        }
        setTimeout(() => {
          if (window.B2BPricing && typeof window.B2BPricing.scheduleVariantRefresh === 'function') {
            window.B2BPricing.scheduleVariantRefresh();
          }
        }, 120);
        setTimeout(() => {
          if (window.B2BPricing && typeof window.B2BPricing.scheduleVariantRefresh === 'function') {
            window.B2BPricing.scheduleVariantRefresh();
          }
        }, 280);
      }

      /* -------------------------
         Helpers: variants present?
      ------------------------- */

      hasSelectableVariants() {
        const flag = this.dataset.hasOnlyDefaultVariant;
        if (flag === 'true') return false;
        const hasFieldset = this.querySelector(
          'fieldset input[type="radio"][data-option-value-id], fieldset input[type="checkbox"][data-option-value-id]'
        );
        const hasSelect = this.querySelector('select option[data-option-value-id]');
        return !!(hasFieldset || hasSelect);
      }

      /* -------------------------
         FoxTheme uses optionValueId
      ------------------------- */

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option:checked, fieldset input:checked'))
          .map(({ dataset }) => dataset.optionValueId)
          .filter(Boolean);
      }

      /* =========================================================
         Linked availability (Color -> Size etc.)
      ========================================================= */

      getVariantData() {
        if (Array.isArray(this.variantData) && this.variantData.length) return this.variantData;
        if (Array.isArray(this.variants) && this.variants.length) return this.variants;

        const productJsonEl =
          document.getElementById('RealSizeProductJson') ||
          document.querySelector('[type="application/json"][id*="ProductJson"]') ||
          document.querySelector('[type="application/json"][data-product-json]');

        if (productJsonEl) {
          try {
            const p = JSON.parse(productJsonEl.textContent || 'null');
            if (p && Array.isArray(p.variants)) return p.variants;
          } catch (_) {}
        }

        const g = window.product || window.__product || window.FoxTheme?.product;
        if (g && Array.isArray(g.variants)) return g.variants;

        return [];
      }

      getOptionGroups() {
        const groups = [];
        const candidates = this.querySelectorAll('select, fieldset');

        candidates.forEach((el, idx) => {
          if (el.tagName === 'SELECT') {
            if (!el.querySelector('option[data-option-value-id]')) return;
            groups.push({ type: 'select', el, index: this.getGroupIndex(el, idx) });
            return;
          }

          if (el.tagName === 'FIELDSET') {
            if (!el.querySelector('input[data-option-value-id]')) return;
            groups.push({ type: 'fieldset', el, index: this.getGroupIndex(el, idx) });
          }
        });

        groups.sort((a, b) => a.index - b.index);
        return groups;
      }

      getGroupIndex(el, fallbackIdx) {
        const raw =
          el.dataset.optionPosition ||
          el.getAttribute('data-option-position') ||
          el.dataset.index ||
          el.getAttribute('data-index');

        const n = parseInt(raw || '', 10);
        if (Number.isFinite(n)) return n > 0 ? n - 1 : 0;

        const selectId = el.id || '';
        const m = selectId.match(/Option-[^-]+-(\d+)$/);
        if (m) return parseInt(m[1], 10);

        return fallbackIdx;
      }

      buildMapsForGroup(group) {
        const labelToId = new Map();
        const idToLabel = new Map();

        if (group.type === 'select') {
          const opts = Array.from(group.el.options);
          opts.forEach((opt) => {
            const id = opt.dataset.optionValueId;
            if (!id) return;
            const label = (opt.value || opt.textContent || '').trim();
            if (!label) return;
            labelToId.set(label, id);
            idToLabel.set(id, label);
          });
        } else {
          const inputs = Array.from(group.el.querySelectorAll('input[data-option-value-id]'));
          inputs.forEach((input) => {
            const id = input.dataset.optionValueId;
            if (!id) return;
            let label = (input.value || '').trim();
            if (!label) {
              const labelEl = input.closest('label');
              label = (labelEl?.textContent || '').replace(/\s+/g, ' ').trim();
            }
            if (!label) return;
            labelToId.set(label, id);
            idToLabel.set(id, label);
          });
        }

        return { labelToId, idToLabel };
      }

      getSelectedIdsByIndex(groups) {
        const selected = new Array(groups.length).fill(null);

        groups.forEach((g) => {
          if (g.type === 'select') {
            const opt = g.el.selectedOptions && g.el.selectedOptions[0];
            selected[g.index] = opt?.dataset?.optionValueId || null;
          } else {
            const checked = g.el.querySelector('input[data-option-value-id]:checked');
            selected[g.index] = checked?.dataset?.optionValueId || null;
          }
        });

        return selected;
      }

      applyAllowedIdsToGroup(group, existingIds, inStockIds) {
        // Keep sold-out combinations visible; only treat missing combinations as unavailable.
        if (group.type === 'select') {
          const opts = Array.from(group.el.options);
          const soldOutLabel = FoxTheme?.variantStrings?.soldOut || 'Sold out';

          opts.forEach((opt) => {
            const id = opt.dataset.optionValueId;
            if (!id) return;
            const exists = existingIds.has(id);
            const inStock = inStockIds.has(id);

            if (!opt.dataset.labelBase) {
              opt.dataset.labelBase = (opt.value || opt.textContent || '').trim();
            }

            // Never hide existing variants (including inventory 0 / sold out).
            opt.hidden = !exists;
            // Keep sold-out options selectable so ATC can show "Sold out".
            opt.disabled = !exists;
            opt.classList.toggle('is-soldout', exists && !inStock);
            opt.textContent =
              exists && !inStock
                ? `${opt.dataset.labelBase} — ${soldOutLabel}`
                : opt.dataset.labelBase;
          });

          const current = group.el.selectedOptions && group.el.selectedOptions[0];
          const currentId = current?.dataset?.optionValueId;
          if (currentId && !existingIds.has(currentId)) {
            const firstExisting = opts.find((o) => o.dataset.optionValueId && existingIds.has(o.dataset.optionValueId));
            if (firstExisting) {
              group.el.value = firstExisting.value;
            }
          }
          return;
        }

        const inputs = Array.from(group.el.querySelectorAll('input[data-option-value-id]'));
        inputs.forEach((input) => {
          const id = input.dataset.optionValueId;
          if (!id) return;
          const exists = existingIds.has(id);
          const inStock = inStockIds.has(id);

          // Missing combination: disable. Sold-out but real: keep selectable, style as sold out.
          input.disabled = !exists;
          input.classList.toggle('disabled', exists && !inStock);

          const labelEl =
            input.closest('label') ||
            (input.id ? this.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
          if (labelEl) {
            labelEl.classList.toggle('is-disabled', !exists || !inStock);
            labelEl.style.display = '';
          }
        });

        const checked = group.el.querySelector('input[data-option-value-id]:checked');
        const checkedId = checked?.dataset?.optionValueId;
        if (checkedId && !existingIds.has(checkedId)) {
          const firstExisting = inputs.find((i) => i.dataset.optionValueId && existingIds.has(i.dataset.optionValueId));
          if (firstExisting) {
            firstExisting.checked = true;
          }
        }
      }

      updateOptionAvailability() {
        const variants = this.getVariantData();
        if (!Array.isArray(variants) || !variants.length) return;

        const groups = this.getOptionGroups();
        if (!groups.length) return;

        const maps = groups.map((g) => this.buildMapsForGroup(g));
        const selectedIds = this.getSelectedIdsByIndex(groups);
        const selectedLabels = selectedIds.map((id, idx) => (id ? maps[idx].idToLabel.get(id) || null : null));

        groups.forEach((group) => {
          const i = group.index;
          const mask = selectedLabels.map((v, idx) => (idx === i ? null : v));

          const existingLabels = new Set();
          const inStockLabels = new Set();

          variants.forEach((v) => {
            if (!v) return;

            const opts = Array.isArray(v.options) && v.options.length
              ? v.options
              : [v.option1, v.option2, v.option3].filter(Boolean);

            if (!opts[i]) return;

            for (let k = 0; k < mask.length; k++) {
              if (mask[k] == null) continue;
              if ((opts[k] || null) !== mask[k]) return;
            }

            const label = String(opts[i]).trim();
            existingLabels.add(label);
            if (v.available !== false) inStockLabels.add(label);
          });

          if (!existingLabels.size) return;

          const existingIds = new Set();
          const inStockIds = new Set();
          existingLabels.forEach((label) => {
            const id = maps[i].labelToId.get(label);
            if (id) existingIds.add(id);
          });
          inStockLabels.forEach((label) => {
            const id = maps[i].labelToId.get(label);
            if (id) inStockIds.add(id);
          });

          if (existingIds.size) this.applyAllowedIdsToGroup(group, existingIds, inStockIds);
        });

        this.syncAddToCartAvailability();
      }

      syncSelectedValueLabels() {
        const groups = this.getOptionGroups();

        groups.forEach((group) => {
          let selectedLabel = '';

          if (group.type === 'select') {
            const opt = group.el.selectedOptions && group.el.selectedOptions[0];
            selectedLabel = (opt?.value || opt?.textContent || '').trim();
          } else {
            const checked = group.el.querySelector('input[data-option-value-id]:checked');
            selectedLabel = (checked?.value || '').trim();
          }

          const wrapper = group.el.closest('.product-form__input');
          const target = wrapper?.querySelector('[data-selected-swatch-value]');
          if (target) target.textContent = selectedLabel;
        });
      }

      /* -------------------------
         Price skeleton logic
      ------------------------- */

      getSectionRoot() {
        return (
          this.closest('[id^="shopify-section-"]') ||
          this.closest('[data-section-id]') ||
          this.closest('.shopify-section') ||
          this.closest('section') ||
          document
        );
      }

      getPriceStableEl() {
        const root = this.getSectionRoot();
        return root.querySelector('.price-stable') || document.querySelector('.price-stable');
      }

      getPriceContentEl() {
        const stable = this.getPriceStableEl();
        if (!stable) return null;
        return stable.querySelector('[id^="price-"]');
      }

      setReady(isReady) {
        const stable = this.getPriceStableEl();
        if (!stable) return;
        stable.classList.toggle('is-ready', !!isReady);
      }

      getSignature() {
        const el = this.getPriceContentEl();
        if (!el) return '';
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        const len = el.innerHTML ? el.innerHTML.length : 0;
        return `${text}__${len}`;
      }

      resetPaintTracking() {
        this._startSig = this.getSignature();
        this._firstSig = '';
        this._sawFirstChange = false;
      }

      waitForBssPaint({ totalTimeoutMs = 3000, afterFirstTimeoutMs = 1200 } = {}) {
        if (!this.hasSelectableVariants()) {
          this.setReady(true);
          return;
        }

        const total = Math.max(400, Number(totalTimeoutMs) || 3000);
        const afterFirst = Math.min(total, Math.max(200, Number(afterFirstTimeoutMs) || 1200));

        const stable = this.getPriceStableEl();
        const priceEl = this.getPriceContentEl();

        if (!stable || !priceEl) {
          this.setReady(true);
          return;
        }

        this.setReady(false);
        this.resetPaintTracking();

        if (this._priceObserver) this._priceObserver.disconnect();

        const unlock = () => {
          this.setReady(true);
          if (this._priceObserver) this._priceObserver.disconnect();
          this._priceObserver = null;
        };

        const scheduleSafety = (ms) => {
          clearTimeout(this._readyTimer);
          this._readyTimer = setTimeout(unlock, ms);
        };

        scheduleSafety(total);

        this._priceObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            const sig = this.getSignature();
            if (!sig) return;
            if (sig === this._startSig) return;

            if (!this._sawFirstChange) {
              this._sawFirstChange = true;
              this._firstSig = sig;
              scheduleSafety(afterFirst);
              return;
            }

            if (sig !== this._firstSig) {
              unlock();
            }
          });
        });

        this._priceObserver.observe(priceEl, { childList: true, subtree: true, characterData: true });
      }

      connectedCallback() {
        this.addEventListener('pointerdown', this._onPointerDownCapture, true);
        this.addEventListener('change', this._onChange);

        queueMicrotask(() => {
          this.updateOptionAvailability();
          this.syncSelectedValueLabels();
          this.waitForBssPaint({ totalTimeoutMs: NANCO_PRICE_TOTAL_MS, afterFirstTimeoutMs: NANCO_PRICE_AFTER_FIRST_MS });

          setTimeout(() => this.triggerB2BRefresh('initial-mount'), 50);
        });
      }

      disconnectedCallback() {
        this.removeEventListener('pointerdown', this._onPointerDownCapture, true);
        this.removeEventListener('change', this._onChange);

        if (this._priceObserver) this._priceObserver.disconnect();
        this._priceObserver = null;

        clearTimeout(this._readyTimer);
        this._readyTimer = null;

        this.clearVariantUpdateWatchers();
      }

      _onPointerDownCapture(event) {
        if (!this.hasSelectableVariants()) return;

        const el = event.target;
        const isOptionInteraction =
          el.matches('input, select, option, label') ||
          el.closest('label') ||
          el.closest('.product-form__input');

        if (!isOptionInteraction) return;

        this.setReady(false);
        this.lockAddToCartUntilVariantUpdates();
      }

      _onChange(event) {
        if (!this.hasSelectableVariants()) return;
        if (this._isApplyingAvailability) return;

        const changedControl = event.target;
        const selectedNode = changedControl.tagName === 'SELECT'
          ? changedControl.selectedOptions[0]
          : changedControl;

        if (!selectedNode || !selectedNode.dataset || !selectedNode.dataset.optionValueId) return;

        this.lockAddToCartUntilVariantUpdates();

        this._isApplyingAvailability = true;
        requestAnimationFrame(() => {
          this.updateOptionAvailability();
          this.syncSelectedValueLabels();
          this._isApplyingAvailability = false;
        });

        this.waitForBssPaint({ totalTimeoutMs: NANCO_PRICE_TOTAL_MS, afterFirstTimeoutMs: NANCO_PRICE_AFTER_FIRST_MS });

        FoxTheme?.pubsub?.publish?.(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: { event, target: changedControl, selectedOptionValues: this.selectedOptionValues },
        });

        setTimeout(() => this.triggerB2BRefresh('onChange'), 20);
        setTimeout(() => this.triggerB2BRefresh('onChange-late'), 160);
      }
    }
  );
}